/**
 * Phase 3y — subscribe to the native live transcription stream.
 *
 * The Swift plugin dispatches a `phase3x-transcript` CustomEvent
 * whenever the on-device SpeechAnalyzer emits a new partial or final
 * result. This hook listens for those events and maintains an
 * ordered list of finalized transcript segments plus the most recent
 * partial (in-progress hypothesis).
 *
 * Partials revise as the recognizer's word-level confidence shifts —
 * they can grow, shrink, or correct themselves. Finals don't change.
 * UI typically renders finals in normal weight and the partial in a
 * dimmer / italic style so the user can see the difference.
 *
 * Transcript is preview-only. Post-session SpeechAnalyzer file-feed
 * (or WhisperKit on older devices) produces the authoritative
 * transcript at Stop, which replaces the preview via the batch path.
 */
import { useEffect, useMemo, useState } from "react";

interface LiveTranscriptEvent {
  sessionId: string;
  startSeconds: number;
  endSeconds: number;
  text: string;
  isFinal: boolean;
}

export interface LiveTranscriptSegment {
  startSeconds: number;
  endSeconds: number;
  text: string;
}

export interface LiveTranscriptState {
  /** Committed transcript slices, ordered by start time. */
  finals: LiveTranscriptSegment[];
  /** Most recent in-progress hypothesis, or null if none pending. */
  partial: LiveTranscriptSegment | null;
}

export function useLiveTranscript(activeSessionId: string | null): LiveTranscriptState {
  const [finals, setFinals] = useState<LiveTranscriptSegment[]>([]);
  const [partial, setPartial] = useState<LiveTranscriptSegment | null>(null);

  useEffect(() => {
    setFinals([]);
    setPartial(null);

    if (!activeSessionId) return;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<LiveTranscriptEvent>).detail;
      if (!detail || detail.sessionId !== activeSessionId) return;

      const segment: LiveTranscriptSegment = {
        startSeconds: detail.startSeconds,
        endSeconds: detail.endSeconds,
        text: detail.text,
      };

      if (detail.isFinal) {
        setFinals((prev) => [...prev, segment]);
        setPartial(null);
      } else {
        setPartial(segment);
      }
    };

    window.addEventListener("phase3x-transcript", handler);
    return () => {
      window.removeEventListener("phase3x-transcript", handler);
    };
  }, [activeSessionId]);

  return useMemo(() => ({ finals, partial }), [finals, partial]);
}
